import React, { useEffect } from 'react';
import {
    AssistantRuntimeProvider,
    useLocalRuntime,
} from '@assistant-ui/react';
import { Thread } from 'components/assistant-ui/thread';
import { MOCK_ASSISTANT_MODE, MOCK_TIME_ASSISTANT_MODE } from 'config/local';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { useKeyboard } from '../../contexts/KeyboardContext';
import { useGraphQLAssistantRuntime } from './runtime/useGraphQLAssistantRuntime';
import { GenerateInteractiveDayPartTool } from './tools/GenerateInteractiveDayPartTool';

type Props = {
    mockRun?: (messages: { role: string; content: string }[]) => Promise<string>;
};

function flattenContent(content: any): string {
    if (Array.isArray(content)) {
        return content
            .map((part) => {
                if (part?.type === 'text') return part.text;
                return ''; // fallback for unhandled types
            })
            .join('\n');
    }
    return typeof content === 'string' ? content : '';
}

const SimpleAssistant: React.FC<Props> = ({ mockRun }) => {
    const { setIgnoreKeyboardEvents } = useKeyboard();
    const gqlRuntime = useGraphQLAssistantRuntime();
    const mockRuntime = useLocalRuntime({
        run: async ({ messages, abortSignal }) => {
            // Convert readonly messages into mutable structure
            const flattenedMessages = messages.map((m) => ({
                role: m.role,
                content: flattenContent(m.content),
            }));

            if (MOCK_ASSISTANT_MODE) {
                const last = flattenedMessages[flattenedMessages.length - 1];
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Mocked assistant says: "${last?.content}"`,
                        },
                    ],
                };
            }

            if (MOCK_TIME_ASSISTANT_MODE) {
                const rawInput = {
                    scheduleOptions: [
                        {
                            date: "2024-06-24T15:00:00Z", 
                            startTime: "10:00",
                            endTime: "12:00"
                        },
                        {
                            date: "2025-06-15T19:00:00Z",
                            dayOfWeek: "THURSDAY",
                            dayPart: "MORNING",
                            startTime: "09:00",
                            endTime: "11:00"
                        },
                        {
                            date: "2025-06-16T08:00:00Z",
                            dayPart: "MORNING",
                            startTime: "09:00",
                            endTime: "11:00"
                        },
                        {
                            date: "2025-06-16T08:00:00Z",
                            dayPart: "EVENING",
                            startTime: "09:00",
                            endTime: "21:00"
                        },
                        {
                            date: "2025-06-17T21:00:00Z",
                            dayOfWeek: "THURSDAY",
                            dayPart: "EVENING",
                            startTime: "19:00",
                            endTime: "21:00"
                        }
                          
                      ]
                  };
                
                const args = {
                    proposedDates: rawInput.scheduleOptions, 
                };
            
                return {
                    content: [
                        {
                            type: "tool-call",
                            toolName: "GenerateInteractiveDayPartTool",
                            toolCallId: "mock-select-daypart",
                            args,
                            argsText: JSON.stringify(args),
                        },
                    ],
                };
            }

            const replyText = mockRun
                ? await mockRun(flattenedMessages)
                : await fetch('/api/assistant', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ messages: flattenedMessages }),
                    signal: abortSignal,
                })
                    .then((res) => res.json())
                    .then((data) => data.messages?.[0]?.content || 'No reply');

            return {
                content: [{ type: 'text', text: replyText }],
            };
        },
    });

    useEffect(() => {
        setIgnoreKeyboardEvents?.(true);
        if (Capacitor.getPlatform() === 'ios') {
            Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(console.error);
            return () => {
                // Optionally restore to default (e.g., Body) when unmounting
                Keyboard.setResizeMode({ mode: KeyboardResize.Body }).catch(console.error);
                setIgnoreKeyboardEvents?.(false);
            };
        }

        return () => {
            setIgnoreKeyboardEvents?.(false);
        };
    }, []);

    const runtime =
        MOCK_ASSISTANT_MODE || MOCK_TIME_ASSISTANT_MODE
            ? mockRuntime
            : gqlRuntime;

    return (
        <div className="relative flex h-full flex-col overflow-hidden" style={{ '--assistant-z': '1' } as React.CSSProperties}>
            <div
                className="absolute left-0 right-0 pointer-events-none"
                style={{
                    top: 0,
                    height: 'env(safe-area-inset-top, 0px)',
                    backgroundColor: 'var(--Plii-Background)',
                    zIndex: 'calc(var(--assistant-z, 0) + 10)',
                }}
            />
            <AssistantRuntimeProvider runtime={runtime}>
                <Thread />
                <GenerateInteractiveDayPartTool />
            </AssistantRuntimeProvider>
        </div>
    );
};

export default SimpleAssistant;
